import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';

// i18n initialization
import './i18n';

// Contexts
import { AuthProvider } from './contexts/AuthContext';

// Components
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';

// Pages
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { PatientsPage } from './pages/PatientsPage';
import { OrdersPage } from './pages/OrdersPage';
import { UsersPage } from './pages/UsersPage';
import { SettingsPage } from './pages/SettingsPage';
import { RBACPage } from './pages/RBACPage';
import { AuditPage } from './pages/AuditPage';
import { SpecialistsPage } from './pages/SpecialistsPage';
import { ClinicalPage } from './pages/ClinicalPage';
import { MedicationsPage } from './pages/MedicationsPage';
import { GeolocationPage } from './pages/GeolocationPage';
import { WhatsAppPage } from './pages/WhatsAppPage';
import { ReportsPage } from './pages/ReportsPage';
import { OCRPage } from './pages/OCRPage';
import { ServicesPage } from './pages/ServicesPage';
import { InventoryPage } from './pages/InventoryPage';
import EnterpriseInventoryPage from './pages/EnterpriseInventoryPage';
import { SpecialistTypesPage } from './pages/SpecialistTypesPage';
import { LocationsPage } from './pages/LocationsPage';
import { ProfilePage } from './pages/ProfilePage';
import { SpecialistCalendarPage } from './pages/SpecialistCalendarPage';
import { FlowsPage } from './pages/FlowsPage';
import { TasksPage } from './pages/TasksPage';
import { AgentQueuesPage } from './pages/AgentQueuesPage';
import { ConversationalQueuesPage } from './pages/ConversationalQueuesPage';
import { APITokensPage } from './pages/APITokensPage';
import { AgentToolsPage } from './pages/AgentToolsPage';
import { CompliancePage } from './pages/CompliancePage';
import { AutomationPage } from './pages/AutomationPage';
import { ScheduledTasksPage } from './pages/ScheduledTasksPage';
import { ReviewsPage } from './pages/ReviewsPage';
import DocumentationPage from './pages/DocumentationPage';

const UnauthorizedPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-gray-900 mb-2">403</h1>
      <p className="text-gray-500 mb-4">No tiene permisos para acceder a esta página</p>
      <a href="/dashboard" className="text-[#E63946] hover:underline">
        Volver al Dashboard
      </a>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          
          {/* Protected routes */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/patients/*"
            element={
              <ProtectedRoute requiredPermissions={['patients.read']}>
                <Layout>
                  <PatientsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/orders/*"
            element={
              <ProtectedRoute requiredPermissions={['orders.read']}>
                <Layout>
                  <OrdersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/specialists/*"
            element={
              <ProtectedRoute requiredPermissions={['specialists.read']}>
                <Layout>
                  <SpecialistsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/specialist-calendar"
            element={
              <ProtectedRoute requiredPermissions={['specialists.read']}>
                <Layout>
                  <SpecialistCalendarPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/clinical/*"
            element={
              <ProtectedRoute requiredPermissions={['clinical.read']}>
                <Layout>
                  <ClinicalPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/medications/*"
            element={
              <ProtectedRoute requiredPermissions={['medications.read']}>
                <Layout>
                  <MedicationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/geolocation/*"
            element={
              <ProtectedRoute requiredPermissions={['geolocation.read']}>
                <Layout>
                  <GeolocationPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/whatsapp/*"
            element={
              <ProtectedRoute requiredPermissions={['whatsapp.read']}>
                <Layout>
                  <WhatsAppPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/reports/*"
            element={
              <ProtectedRoute requiredPermissions={['reports.read']}>
                <Layout>
                  <ReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/users/*"
            element={
              <ProtectedRoute requiredPermissions={['users.read']}>
                <Layout>
                  <UsersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/rbac/*"
            element={
              <ProtectedRoute requiredPermissions={['roles.read']}>
                <Layout>
                  <RBACPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/audit/*"
            element={
              <ProtectedRoute requiredPermissions={['audit.read']}>
                <Layout>
                  <AuditPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/settings/*"
            element={
              <ProtectedRoute requiredPermissions={['config.read']}>
                <Layout>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/ocr/*"
            element={
              <ProtectedRoute requiredPermissions={['ocr.process']}>
                <Layout>
                  <OCRPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/services/*"
            element={
              <ProtectedRoute requiredPermissions={['services.read']}>
                <Layout>
                  <ServicesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/inventory/*"
            element={
              <ProtectedRoute requiredPermissions={['inventory.read']}>
                <Layout>
                  <InventoryPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/compliance/*"
            element={
              <ProtectedRoute requiredPermissions={['inventory.read']}>
                <Layout>
                  <CompliancePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/enterprise-inventory/*"
            element={
              <ProtectedRoute requiredPermissions={['inventory.read']}>
                <Layout>
                  <EnterpriseInventoryPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/specialist-types/*"
            element={
              <ProtectedRoute requiredPermissions={['specialists.read']}>
                <Layout>
                  <SpecialistTypesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/locations/*"
            element={
              <ProtectedRoute requiredPermissions={['config.read']}>
                <Layout>
                  <LocationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProfilePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/flows/*"
            element={
              <ProtectedRoute requiredPermissions={['config.read']}>
                <Layout>
                  <FlowsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/tasks/*"
            element={
              <ProtectedRoute requiredPermissions={['config.read']}>
                <Layout>
                  <TasksPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/agent-queues/*"
            element={
              <ProtectedRoute requiredPermissions={['config.read']}>
                <Layout>
                  <AgentQueuesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/conversations/*"
            element={
              <ProtectedRoute requiredPermissions={['config.read']}>
                <Layout>
                  <ConversationalQueuesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/api-tokens/*"
            element={
              <ProtectedRoute requiredPermissions={['config.read']}>
                <Layout>
                  <APITokensPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/agent-tools/*"
            element={
              <ProtectedRoute requiredPermissions={['config.read']}>
                <Layout>
                  <AgentToolsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/automation/*"
            element={
              <ProtectedRoute requiredPermissions={['config.read']}>
                <Layout>
                  <AutomationPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/scheduled-tasks/*"
            element={
              <ProtectedRoute requiredPermissions={['config.read']}>
                <Layout>
                  <ScheduledTasksPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/reviews/*"
            element={
              <ProtectedRoute requiredPermissions={['reports.read']}>
                <Layout>
                  <ReviewsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Documentation */}
          <Route
            path="/documentation"
            element={
              <ProtectedRoute>
                <Layout>
                  <DocumentationPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      
      {/* Toast notifications */}
      <Toaster 
        position="top-right" 
        richColors 
        closeButton
        toastOptions={{
          style: {
            fontFamily: 'IBM Plex Sans, sans-serif'
          }
        }}
      />
    </AuthProvider>
  );
}

export default App;
